import React, { useState, useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import styles from "../../../../styles/paymentForm.module.scss";
import { customerServiceNumberFormatted } from '../../../../utility/utils/formatter';
import Globals from "../../../../json/fallbackglobalvariables.json";
import { useSelector } from "react-redux";
import addPaymentMethodsClient from "../../../../utility/apiClients/payments/addPaymentMethodsClient";
import { isEmailValid } from "../../../../utility/utils/dateUtil";
import OverlayLoader from "../../../loader/overlayLoader";
import { setCookie, deleteCookie } from '../../../../utility/utils/cookie';
import { GetAccessToken, GetPaymentToken } from '../../../../utility/apiClients/account/paymetricClient';
import { decryptData } from '../../../../utility/utils/hashing';
import ErrorBlock from '../../errorMessage';
import { encryptData } from "../../../../utility/utils/hashing";
import Loader from "../../../loader";

const formatDateYYYYMMDD = (d = new Date()) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}${mm}${dd}`;
};

const detectCardType = (cardNumber = "") => {
  const digits = cardNumber.replace(/\D/g, "");
  if (!digits) return "";
  if (/^4/.test(digits)) return "VISA";
  if (/^5[1-5]/.test(digits)) return "MC";
  if (/^3[47]/.test(digits)) return "AMEX";
  if (/^6(?:011|5)/.test(digits)) return "DISC";
  return "";
};

const months = Array.from({ length: 12 }, (_, i) => {
  const m = i + 1;
  return {
    value: String(m).padStart(2, "0"),
    label: String(m).padStart(2, "0"),
  };
});

const years = Array.from({ length: 12 }, (_, i) => {
  const y = new Date().getFullYear() + i;
  return { value: String(y), label: String(y) };
});

export default function PaymentForm({
  onSubmit:onParentSubmit,
  totalAmount = 0.00,
  salesAgrmtObj = null,
  flag=""
}) {
  const labels = Globals.acc;
  
  const states = Globals.stateList.split(",");
  const [paymentData, setPaymentData] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [iframeURLToken, setIframeURLToken] = useState({IFrameUrl: '', AccessToken: ''});
  const [iframeLoading, setIframeLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const iFrameRef = useRef(null);
  const iFrameContRef = useRef(null);
  const [paymentType, setPaymentType] = useState('card');
  const customerSummary = useSelector((state) => state.login.customerInfo);
  const customerDetails = useSelector((state) => state.getCustomerDetails.customerDetails.SalesAgreementVer5PPEPList);
  const customerEmail = customerSummary && customerSummary?.[0]?.EmailAddress;
  const name = (customerSummary && customerSummary?.[0]?.BusinessName) || (`${customerSummary?.[0]?.FirstName || ''} ${customerSummary?.[0]?.LastName || ''}`.trim());
  const selectedServiceAddress = useSelector((state) => state.accountSummary.selectedServiceAddress);
  const customerZip = customerSummary && customerSummary?.[0]?.ServiceAddresses?.[0]?.postalCode;
  const cusZip = customerDetails.length ? customerDetails?.[0].sazip : customerZip;

  /* get current selected customer no. with service address */
  const [serviceAddress, customerNumber] = useMemo(() => {
    let custNumber = "";
    let serviceAddr = "";
    if(selectedServiceAddress != null){
      [serviceAddr, custNumber] = selectedServiceAddress.split(" | ");
    } else {
      custNumber = customerSummary?customerSummary?.[0]?.CustomerNumber:"";
      serviceAddr = customerSummary?customerSummary?.[0]?.ServiceAddresses[0].address:"";
    }
    return [serviceAddr, custNumber];
  }, [selectedServiceAddress, customerSummary]);

  const getIframeUrl = async(paymentType) => {
      setIsLoading(true);
      setIframeLoading(true);
      try{    
          const response = await GetAccessToken(paymentType, customerNumber || "");            
          const accessToken = decryptData(response?.data?.AccessToken, "step1");
          const iFrameUrl = response.data.Signature;            
          if(paymentType === 'card'){
            setCookie('IUcard', iFrameUrl, 20);
          } else {
            setCookie('IUbank', iFrameUrl, 20);
          }
          setIframeURLToken({IFrameUrl: iFrameUrl, AccessToken: accessToken});
      } catch(err){
          console.log("error in fetching iframe url: ", err);
          setIframeLoading(false);
      } finally {
          setIsLoading(false);
      }
  }

  const handlePaymentTypeChange = async (type) => {
    setPaymentType(type);
    setError('');
    setErrMsg('');
    setIframeURLToken({IFrameUrl: '', AccessToken: ''})
    
    setIframeLoading(true);
    if(type === 'bank') {
      await getIframeUrl('bank');
    }else if (type === 'card') {
      await getIframeUrl('card');
    }
  };

  useEffect(() => {
    deleteCookie('IUcard');
    deleteCookie('IUbank');

    // Load XiSecurenet script dynamically
    const existingScript = document.querySelector(`script[src="${process.env.REACT_APP_XiSecurenet_Script_Src}"]`);
    if (!existingScript) {
        const script = document.createElement('script');
        script.src = process.env.REACT_APP_XiSecurenet_Script_Src;
        script.async = true;
        document.body.appendChild(script);
    }
    (async () => await getIframeUrl(paymentType))();

    // Listen for messages from Paymetric iframe - iOS Safari compatible
    const handleMessage = (event) => {
      try {
        if (event && event.data && typeof event.data === 'object') {
          setPaymentData(event.data);
        }
      } catch (error) {
        console.error('Message handler error:', error);
      }
    };
    
    // iOS Safari requires explicit options object
    const listenerOptions = { passive: true, capture: false };
    window.addEventListener('message', handleMessage, listenerOptions);
    
    return () => {
      try {
        window.removeEventListener('message', handleMessage, listenerOptions);
      } catch (error) {
        // Fallback for cleanup
        window.removeEventListener('message', handleMessage);
      }

      // Cleanup function to remove the script if it was added
      const script = document.querySelector(`script[src="${process.env.REACT_APP_XiSecurenet_Script_Src}"]`);
      if (script) {
          document.body.removeChild(script);
      }
    };
  }, []);
  
  useEffect(() => {
    window.onerror = function(message, source, lineno, colno, error) {
      // Code to handle the error
      
      const errorOverlayID = "webpack-dev-server-client-overlay"
      const iframeElement = window.parent.document.querySelector("iframe#" + errorOverlayID);

      if(iframeElement && iframeElement.tagName === 'IFRAME' && iframeElement.contentWindow.document.body.textContent.includes("Script error.\nhandleError")) {
        iframeElement.parentNode.removeChild(iframeElement);
      }

      // Return true to prevent the default browser error handling (e.g., logging to console)
      // Note: returning true stops propagation in modern browsers
      return true;
    };
  }, []);

  const {
    register,
    getValues,
    handleSubmit,
    control,
    watch,
    formState: { errors },
    reset,
    setError,
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      address: "",
      city: "",
      state: "",
      zip: "",
    },
  });

  const watchCardNumber = watch("cardNumber");

  const buildRequestBody = async (values, selectedTab) => {
    let requestBody = {};
    let salesAgrmtLst = salesAgrmtObj;
    if (salesAgrmtObj && !Array.isArray(salesAgrmtObj)) {
      salesAgrmtLst = salesAgrmtObj.value;
    }

    const curDateString = formatDateYYYYMMDD(new Date());

    if (selectedTab === "card") {
      const expMonth = (values.expMonth || "").toString().padStart(2, "0");
      const expYear = (values.expYear || "").toString();
      const accCcdExpDte = expYear && expMonth ? `${expYear}${expMonth}01` : "";

      const accTypOfCcd = detectCardType(values.cardNumber || "");

      requestBody = {
        add_autopay: {
          in: {
            accBilAddrSID: "0",
            accCcdExpDte,
            accCcdSid: 0,
            accCusnum: customerNumber || "",
            accEffDte: curDateString,
            accFOFInd: "YES",
            accPtyID: "0",
            accPymtOpt: "ACC",
            accRefNum: (values.cardNumber || "").replace(/\s+/g, ""),
            accRoutingNum: "",
            accTypOfCcd,
            accTypOfPymnt: "CCD",
          }
        },
      };
    } else if (selectedTab === "bank") {
      const routingNum = (values.routingNumber || "").toString().trim();

      if (!routingNum || routingNum.length !== 9) {
        const err = new Error(
          "Routing Number must be exactly 9 digits. Please try again."
        );
        err.genericMessage = err.message;
        throw err;
      }

      requestBody = {
        add_autopay: {
          in: {
            accBilAddrSID: "0",
            accCcdExpDte: "",
            accCcdSid: 0,
            accCusnum: customerNumber || "",
            accEffDte: curDateString,
            accFOFInd: "YES",
            accPtyID: "0",
            accPymtOpt: "ACC",
            accRefNum: values.accountNumber || "",
            accRoutingNum: routingNum,
            accTypOfCcd: "CHK",
            accTypOfPymnt: "ACH",
          }
        },
      };
    } else {
      throw new Error("No payment type selected");
    }

    if (salesAgrmtLst && salesAgrmtLst.length) {
      requestBody.add_autopay.autoPayList = {
        accAction: "START",
        accSalesAgrmt: salesAgrmtLst,
      };
    }

    let services_enrolled = [];
    if (salesAgrmtObj && salesAgrmtObj.data) {
      services_enrolled = salesAgrmtLst.map(
        (s) => salesAgrmtObj.data[s].svcLineDesc
      );
    }

    const todaysDate = formatDateYYYYMMDD(new Date(), "-");
    
    let emailRequest = {};
    const now = new Date();
    const formattedDate = now.toISOString().slice(0, 19);
    const customProperties = {"url": process.env.REACT_APP_DOMAIN_URL };
    let finalEmail = (isEmailValid(customerEmail) && customerEmail) || null;
    setEmail(finalEmail);

    emailRequest.email = {
      activity: {
        subscriber: {
          uid: finalEmail,
          contacts: [
            {
              contact_type: "email",
              contact_value: finalEmail,
              Subscription_status: "active",
            },
          ],
        },
        event: "msdynmkt_comm_payment_added_211706022",
        timestamp: formattedDate,
        properties: {
          first_name: name,
          ...customProperties,
        },
      },
    };

    const newRequestBody = {
      ...requestBody,
      email: emailRequest.email,
    };

    return newRequestBody;
  };

  const onLoadHandler = () => {
    const $XIFrame = window.$XIFrame;
    const iFrameId = 'paymetric-iframe-id';
    const targetUrl = iframeURLToken.IFrameUrl;

    if (!$XIFrame) {
      setErrMsg('Payment system not initialized');
      setIframeLoading(false);
      return;
    }

    $XIFrame.onload({
      iFrameId: iFrameId,
      targetUrl: targetUrl,
      autosizeheight: true,
      onLoadSuccess: function () {
        setIframeLoading(false);
      },
      onError: function () {
        setIframeLoading(false);
      }
    });
  }

  const submit = async (e) => {
    if(e){
      e.preventDefault(); //prevent default submit
    }

    if(Array.isArray(salesAgrmtObj?.value) && salesAgrmtObj?.value?.length === 0){
      setIsSubmitting(false);
      return;
    }

    
    setErrMsg('');

    // Get the appropriate iframe and access token based on payment type
    const iFrameId = 'paymetric-iframe-id';
    const accessToken = iframeURLToken.AccessToken;
    const targetUrl = iframeURLToken.IFrameUrl;
    
    if (!accessToken) {
      setErrMsg('Access token not available');
      setIsSubmitting(false);
      return;
    }

    const allFormValues = getValues();

    const responsePacketReqZip = allFormValues["zip"] !== '' && allFormValues["zip"]?.length === 5 ? allFormValues["zip"] : cusZip || '00000';

    // Use $XIFrame to submit the payment form
    const $XIFrame = window.$XIFrame;
    if (!$XIFrame) {
      setErrMsg('Payment system not initialized');
      setIsSubmitting(false);
      return;
    }

    $XIFrame.submit({
      iFrameId: iFrameId,
      targetUrl: targetUrl,
      onSuccess: (msg) => {
        const response = JSON.parse(msg);
        setIsSubmitting(true);
        if (response.data && response.data.HasPassed) {
          const encryptedAccessToken = encryptData(accessToken, "step2");
          GetPaymentToken(
            encryptedAccessToken, 
            customerNumber, 
            totalAmount, 
            responsePacketReqZip
          ).then(async tokenResponse => {
              if (tokenResponse.rawData && tokenResponse.rawData[3] && tokenResponse.rawData[3].AuthorizedCard === false) {
                setErrMsg('Payment failed: Invalid card details. Please try again or use a different card');
                setIsSubmitting(false);
                return;
              }

              setErrMsg("");
              if (typeof onParentSubmit === 'function' && flag === "") {
                // onParentSubmit(tokenResponse.data, {...allFormValues}, true,tokenResponse.rawData[3], paymentType);
                await onParentSubmit(e, tokenResponse.data);
                return;
                
              } else {

                if(flag === "prePayPayment"){
                  onParentSubmit(tokenResponse.data, {...allFormValues}, true, tokenResponse.rawData[3], paymentType);
                  return;
                }
                
                if(flag==="easyPayADDChange"){
                  await onParentSubmit(e, tokenResponse.data);
                  return;
                }

                try {
                  setIsLoading(true);
                  const values = {
                    ...allFormValues
                  };

                  // Build payload with active tab fields (UI-level payload)
                  const uiPayload = { paymentType: paymentType, ...values };

                  // Build backend request body inside component
                  const requestBody = await buildRequestBody(values, paymentType);

                  const response = await addPaymentMethodsClient(requestBody);

                  if (response && response.status === 200) {
                    setSuccessMessage(
                      `Your payment method was saved to your account successfully. A confirmation email has been sent to ${
                        email || "your email"
                      }.`
                    );
                    setShowSuccessModal(true);
                  } else {
                    console.warn("Unexpected response", response);
                  }

                  if (onParentSubmit) {
                    onParentSubmit({ uiPayload, requestBody });
                  } else {
                  }

                  reset();
                } catch (err) {
                  if (err && err?.genericMessage && paymentType === "bank") {
                    setErrMsg("routingNumber", {
                      type: "manual",
                      message: err.genericMessage,
                    });
                  } else {
                    setErrMsg(`An error has occurred in processing the payment. Please contact Customer Care at ${customerServiceNumberFormatted}`);
                  }
                } finally {
                  setIsLoading(false); // hide loader
                }
              }
            })
            .catch(error => {
              console.error('GetPaymentToken error:', error);
              setErrMsg('Payment failed: There was an error while authorizing card details.');
              setIsSubmitting(false);
            }).finally(() => {
              setIsSubmitting(false);
            });
        } 
        
        else {
          setErrMsg('Payment failed: There was an error submitting payment.');
          setIsSubmitting(false);
        }
      },
      onError: (msg) => {
        console.error('XIFrame error:', msg);
        setErrMsg('Payment failed: Server error occurred.');
        setIsSubmitting(false);
      }
    });
  };

  

  return (
    <>
      {isLoading && (
        <div
          className={styles.loaderOverlay}
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <OverlayLoader isOpen={isLoading} />
        </div>
      )}

      {errMsg && <ErrorBlock labels={labels} errMsg={errMsg} />}

      {showSuccessModal && (
        <div>
          <h2 style={{ color: "green" }}>Success</h2>
          <p>{successMessage}</p>
        </div>
      )}

      {showSuccessModal && (
        <div className={styles.successOverlay} role="dialog" aria-modal="true">
          <div className={styles.successModal}>
            <button
              className={styles.successClose}
              aria-label="Close success"
              onClick={() => setShowSuccessModal(false)}
            >
              ×
            </button>

            <div className={styles.successBody}>
              <h2 className={styles.successTitle}>Success</h2>
              <p className={styles.successText}>{successMessage}</p>
              <div className={styles.successActions}></div>
            </div>
          </div>
        </div>
      )}
      <form id='PaymentForm' className={`${styles["form"]} container noValidate`}>
        <div>
          <section className='container-fluid'>
            <div className='row'>
              <div className={`align-items-end col d-flex ${styles["p-0"]} ${styles["paymetric--form_radio"]}`}>
                <div 
                  className={`${styles["d-inline-block"]} ${styles["float-left"]} ${styles["position-relative"]}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handlePaymentTypeChange('card');
                  }}
                  style={{ cursor: 'pointer', touchAction: 'manipulation' }}
                >
                  <input 
                    type='radio' 
                    id='creditcard' 
                    value='creditcard' 
                    checked={paymentType === 'card'} 
                    style={{ 
                      display: 'none',
                      pointerEvents: 'none'
                    }}
                  />
                  <label 
                    htmlFor='creditcard' 
                    className={`${styles[paymentType === "card" ? "bg-white-clr" : "bg-gray-light"]} ${styles["gfs-16"]} ${styles["gfw-7"]} ${styles["glh-1"]} ${styles["m-0"]} ${styles["p-14"]}`} 
                    style={{ 
                      cursor: 'pointer', 
                      minHeight: '44px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      userSelect: 'none', 
                      WebkitTapHighlightColor: 'transparent',
                      touchAction: 'manipulation',
                      pointerEvents: 'all'
                    }}
                  >
                    Credit or Debit Card
                  </label>
                </div>
                <div 
                  className={`${styles["d-inline-block"]} ${styles["float-left"]} ${styles["position-relative"]}`}
                  onClick={() => handlePaymentTypeChange('bank')}
                  style={{ cursor: 'pointer', touchAction: 'manipulation' }}
                >
                  <input 
                    type='radio' 
                    id='bankaccount' 
                    value='bankaccount' 
                    checked={paymentType === 'bank'} 
                    style={{ 
                      display: 'none',
                      pointerEvents: 'none'
                    }}
                  />
                  <label 
                    htmlFor='bankaccount' 
                    className={`${styles[paymentType === "bank" ? "bg-white-clr" : "bg-gray-light"]} ${styles["gfs-16"]} ${styles["gfw-7"]} ${styles["glh-1"]} ${styles["m-0"]} ${styles["p-14"]}`} 
                    style={{ 
                      cursor: 'pointer', 
                      minHeight: '44px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      userSelect: 'none', 
                      WebkitTapHighlightColor: 'transparent',
                      touchAction: 'manipulation',
                      pointerEvents: 'all'
                    }}
                  >
                    Bank Account
                  </label>
                </div>
              </div>
            </div>
            
            <div className={`${styles["paymetric--form_wrapper"]} ${styles["bg-white"]} ${styles["paymetric--mb-20"]} ${styles["py-20"]} row`}>
              <div className='col-12 col-lg-7'>
                {/* alert message when card authorization fails */}
                {errMsg && (
                  <div className="alert-message">
                    <p><img src="/images/Warning.png" className="img-fluid" alt="Warning" />{errMsg}</p>
                  </div>
                )}
                
                <div className='row' style={{marginLeft: "0px"}}>
                  <div className='col-12'>
                    
                    {iframeURLToken.IFrameUrl || !iframeLoading ? 
                      <iframe
                        src={iframeURLToken.IFrameUrl}
                        id={'paymetric-iframe-id'}
                        className='payment-iframe w-100'
                        onLoad={onLoadHandler}
                        title="Payment Iframe"
                        ref={iFrameContRef}
                      />
                      : <Loader height={`30vh`} />
                    }
                    
                  </div>
                  
                  {paymentType === 'card' && (
                    <div className='col-12'>
                      <div className={styles["paymetric--form"]}>
                        <div className={`${styles["paymetric--mb-20"]} row`}>
                          <div className='col'>
                            <input 
                              placeholder="Billing Address"
                              {...register("address")}
                              className={`${styles["form-control"]}`}
                              id='billingAddress'
                              aria-describedby='billingAddress'
                            />
                            
                          </div>
                        </div>
                        
                        <div className='row'>
                          <div className={`col-12 col-md ${styles["paymetric--mb-20"]} ${styles["mb-md-0"]}`}>
                            <input 
                              placeholder="City"
                              {...register("city")}
                              className={`${styles["form-control"]}`}
                              id='billingCity'
                              aria-describedby='billingCity'
                            />
                            
                          </div>
                          
                          <div className={`col-12 col-md ${styles["paymetric--mb-20"]} ${styles["mb-md-0"]}`}>
                            
                            <select
                              {...register("state")}
                              className={`${styles["form-control"]} rounded-0 mt-0`}
                              id='billingState'
                            >
                              <option value="">State</option>
                              {states.map((s) => (
                                <option key={s} value={s}>
                                  {s}
                                </option>
                              ))}
                            </select>
                            
                          </div>
                          
                          <div className={`col-12 col-md ${styles["paymetric--mb-20"]} ${styles["mb-md-0"]}`}>
                            <input 
                              placeholder="Zip Code"
                              {...register("zip", {
                                pattern: {
                                  value: /^\d{5}(-\d{4})?$/,
                                  message: "Invalid zip",
                                },
                              })}
                              className={`${styles["form-control"]}`}
                              id='billingZip'
                              aria-describedby='billingZip'
                            />
                            
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className='col-12 col-lg-5'>
                <div className={paymentType === 'card' ? 'd-block' : 'd-none'}>
                  <div className={`col-12 ${styles["g-font-italic"]} ${styles["gfs-14"]} ${styles["gfw-3"]} ${styles["paymetric--mb-20"]}`}>
                    TruGreen accepts the following cards
                  </div>
                  <div className='col-12'>
                    <div className={`${styles["form-row"]} ${styles["paymetric--mb-20"]} `}>
                      <div className={`col mcIndicator ${styles["paymetric--form_card"]} ${styles["paymetric--form_card-sm"]}`} >
                        <img src="/images/myAccount/paymentForm/MasterCard_lg.png" alt="Mastercard" className={styles["cardLogo"]} loading="lazy" />
                      </div>
                      <div className={`col ${styles["paymetric--form_card"]} ${styles["paymetric--form_card-sm"]} viIndicator`}>
                        <img src="/images/myAccount/paymentForm/Visa_lg.png" alt="Visa" className={styles["cardLogo"]} loading="lazy" />
                      </div>
                      <div className={`axIndicator col ${styles["paymetric--form_card"]} ${styles["paymetric--form_card-sm"]}`}>
                        <img src="/images/myAccount/paymentForm/AmericanExpress_lg.png" alt="American Express" className={styles["cardLogo"]} loading="lazy" />
                      </div>
                      <div className={`col ${styles["paymetric--form_card"]} ${styles["paymetric--form_card-sm"]} diIndicator`}>
                        <img src="/images/myAccount/paymentForm/Discover_lg.png" alt="Discover" className={styles["cardLogo"]} loading="lazy" />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className={paymentType === 'bank' ? 'd-block' : 'd-none'}>
                  <div className='col-12 text-center'>
                    <img src='/images/myAccount/CheckSample.png' className='img-fluid' alt="Check Sample" />
                  </div>
                </div>
              </div>
            </div>
          </section>
          
          <div id='paymetricSubmitCancel' className='row'>
            <div className='col-12 p-0 d-flex justify-content-center'>
              <div className='text-right'>
                <button 
                  type="button" 
                  className={`btn btn-lg ${styles["btn-primary"]} ${styles["btn-orange"]} rounded-0 ${
                    (Array.isArray(salesAgrmtObj?.value) && salesAgrmtObj?.value?.length === 0) ? 'disabled' : ''
                  }`}
                  onClick={(e) => submit(e)}
                  disabled={isSubmitting || (Array.isArray(salesAgrmtObj?.value) && salesAgrmtObj?.value?.length === 0)}
                >
                  {isSubmitting ? 'Processing...' : 'Add the payment method'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
      
    </>
  );
}
